import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Plus, Pencil, Trash2, AlertTriangle, FileText, Receipt, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { inmueblesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import { DocumentosInmueble } from './DocumentosInmueble';

const eur = (v) => `${Number(v || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const TIPOS = [['vivienda', 'Vivienda'], ['local', 'Local'], ['oficina', 'Oficina'],
  ['garaje', 'Garaje'], ['trastero', 'Trastero'], ['nave', 'Nave'], ['rustico', 'Rústico']];
const FORMAS = [['compra', 'Compra'], ['herencia', 'Herencia'], ['donacion', 'Donación'],
  ['otra', 'Otra']];
const MODALIDADES = [['uso_distinto', 'Uso distinto de vivienda (local, oficina, garaje)'],
  ['vivienda_habitual', 'Vivienda habitual (art. 2.1 LAU)'],
  ['temporada', 'Temporada (art. 3 LAU)'],
  ['corta_duracion', 'Corta duración (≤ 30 noches)']];
const TIPOS_ARRENDATARIO = [['particular', 'Particular'], ['empresario', 'Empresario o profesional'],
  ['entidad', 'Sociedad o entidad'], ['administracion', 'Administración pública']];
const ETIQUETA_CLASE = {
  intereses: 'Intereses de financiación', reparacion: 'Reparación y conservación',
  tributos: 'Tributos y tasas (IBI, basuras)', servicios: 'Comunidad, seguros y servicios',
  dudoso_cobro: 'Saldos de dudoso cobro', muebles: 'Muebles (se amortizan al 10 %)',
  mejora: 'Mejora (no es gasto: se amortiza al 3 %)', otros: 'Otros gastos',
};

const INMUEBLE_VACIO = {
  direccion: '', referencia_catastral: '', municipio: '', tipo: 'local',
  porcentaje_titularidad: 100, forma_adquisicion: 'compra', fecha_adquisicion: '',
  precio_adquisicion: 0, gastos_adquisicion: 0, valor_catastral_total: 0,
  valor_catastral_construccion: 0, anio_revision_catastral: '', zona_tensionada: false,
  mejoras: [], muebles: [], notas: '', activo: true,
};

const CONTRATO_VACIO = {
  arrendatario_nombre: '', arrendatario_nif: '', arrendatario_tipo: 'entidad',
  arrendatario_nacimiento: '', fecha_celebracion: '', fecha_inicio: '', fecha_fin: '',
  modalidad: 'uso_distinto', servicios_hoteleros: false, renta_mensual: 0,
  renta_anterior: '', indice_referencia: '', primer_alquiler: false, gran_tenedor: false,
  zona_tensionada: false, rehabilitada_2anios: false, alquiler_social: false,
  prorroga_tacita: false, con_retencion: true, reduccion_manual: '', notas: '', activo: true,
};

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// Panel de inmuebles en arrendamiento: ficha, contratos y rendimiento del ejercicio.
const InmueblesPanel = ({ ejercicio }) => {
  const [inmuebles, setInmuebles] = useState([]);
  const [rendimiento, setRendimiento] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);     // ficha en edición
  const [contrato, setContrato] = useState(null);     // contrato en edición
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [docs, setDocs] = useState(null);             // {inmuebleId, contratoId, tipo, titulo}

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [fichas, calculo] = await Promise.all([
        inmueblesAPI.listar(),
        inmueblesAPI.rendimiento(ejercicio),
      ]);
      setInmuebles(fichas.inmuebles || []);
      setTiposDocumento(fichas.tipos_documento || []);
      setRendimiento(calculo);
    } catch (e) {
      toast({ title: 'No se han podido cargar los inmuebles', variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  }, [ejercicio]);

  useEffect(() => { cargar(); }, [cargar]);

  const porInmueble = useMemo(() => {
    const mapa = {};
    (rendimiento?.inmuebles || []).forEach((r) => { mapa[r.inmueble_id] = r; });
    return mapa;
  }, [rendimiento]);

  const guardarInmueble = async () => {
    const datos = {
      ...editando,
      porcentaje_titularidad: Number(editando.porcentaje_titularidad || 100),
      precio_adquisicion: Number(editando.precio_adquisicion || 0),
      gastos_adquisicion: Number(editando.gastos_adquisicion || 0),
      valor_catastral_total: Number(editando.valor_catastral_total || 0),
      valor_catastral_construccion: Number(editando.valor_catastral_construccion || 0),
      anio_revision_catastral: num(editando.anio_revision_catastral),
      fecha_adquisicion: editando.fecha_adquisicion || null,
    };
    try {
      if (editando.id) {
        await inmueblesAPI.actualizar(editando.id, datos);
        toast({ title: 'Inmueble guardado' });
      } else {
        const creado = await inmueblesAPI.crear(datos);
        toast({ title: 'Inmueble guardado',
                description: 'Ahora puedes adjuntar la escritura y el resto de documentos.' });
        setDocs({ inmuebleId: creado.id, contratoId: null, tipo: 'escritura',
                  titulo: creado.direccion });
      }
      setEditando(null);
      cargar();
    } catch (e) {
      toast({ title: 'No se ha podido guardar', description: e?.response?.data?.detail,
              variant: 'destructive' });
    }
  };

  const guardarContrato = async () => {
    const datos = {
      ...contrato,
      renta_mensual: Number(contrato.renta_mensual || 0),
      renta_anterior: num(contrato.renta_anterior),
      indice_referencia: num(contrato.indice_referencia),
      reduccion_manual: num(contrato.reduccion_manual),
      arrendatario_nacimiento: contrato.arrendatario_nacimiento || null,
      fecha_celebracion: contrato.fecha_celebracion || null,
      fecha_inicio: contrato.fecha_inicio || null,
      fecha_fin: contrato.fecha_fin || null,
    };
    try {
      if (contrato.id) {
        await inmueblesAPI.actualizarContrato(contrato.id, datos);
        toast({ title: 'Contrato guardado' });
      } else {
        const creado = await inmueblesAPI.crearContrato(contrato.inmueble_id, datos);
        toast({ title: 'Contrato guardado',
                description: 'Adjunta ahora el PDF del contrato firmado y sus anexos.' });
        setDocs({ inmuebleId: contrato.inmueble_id, contratoId: creado.id, tipo: 'contrato',
                  titulo: creado.arrendatario_nombre });
      }
      setContrato(null);
      cargar();
    } catch (e) {
      toast({ title: 'No se ha podido guardar el contrato',
              description: e?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const borrarInmueble = async (id) => {
    if (!window.confirm('¿Borrar la ficha de este inmueble?')) return;
    try {
      await inmueblesAPI.borrar(id);
      cargar();
    } catch (e) {
      toast({ title: 'No se ha podido borrar', description: e?.response?.data?.detail,
              variant: 'destructive' });
    }
  };

  const asignarFactura = async (facturaId, inmuebleId) => {
    await inmueblesAPI.asignarFactura(facturaId, { inmueble_id: Number(inmuebleId) });
    cargar();
  };

  const asignarGasto = async (gastoId, inmuebleId, clase) => {
    await inmueblesAPI.asignarGasto(gastoId, {
      inmueble_id: inmuebleId ? Number(inmuebleId) : null,
      clase_gasto_inmueble: clase || null,
    });
    cargar();
  };

  if (cargando) return <p className="text-sm text-slate-500 p-4">Cargando inmuebles…</p>;

  const t = rendimiento?.totales || {};

  return (
    <div className="space-y-4" data-testid="inmuebles-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Inmuebles en arrendamiento · {ejercicio}
          </h3>
          <p className="text-xs text-slate-500">
            Rendimiento del capital inmobiliario para el modelo 100: ingresos, gastos del art. 23.1,
            amortización del 3 % y reducción del art. 23.2.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditando({ ...INMUEBLE_VACIO })}
                data-testid="inmueble-nuevo-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo inmueble
        </Button>
      </div>

      {rendimiento?.inmuebles?.length > 0 && (
        <Card data-testid="inmuebles-totales">
          <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><p className="text-xs text-slate-500">Ingresos</p>
              <p className="text-lg font-bold">{eur(t.ingresos)}</p></div>
            <div><p className="text-xs text-slate-500">Gastos deducidos</p>
              <p className="text-lg font-bold">{eur(t.gastos_deducidos)}</p></div>
            <div><p className="text-xs text-slate-500">Rendimiento neto</p>
              <p className="text-lg font-bold">{eur(t.rendimiento_neto)}</p></div>
            <div><p className="text-xs text-slate-500">Tras la reducción</p>
              <p className="text-lg font-bold text-emerald-700"
                 data-testid="inmuebles-rendimiento-reducido">{eur(t.rendimiento_reducido)}</p></div>
          </CardContent>
        </Card>
      )}

      {(rendimiento?.facturas_sin_inmueble?.length > 0
        || rendimiento?.gastos_sin_clasificar?.length > 0) && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="inmuebles-sin-asignar">
          <CardHeader><CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Pendiente de asignar
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {rendimiento.facturas_sin_inmueble.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" /> {f.fecha} · {f.numero_factura} ·
                  {' '}{f.cliente_nombre} · {eur(f.base_imponible)}
                </span>
                <Select onValueChange={(v) => asignarFactura(f.id, v)}>
                  <SelectTrigger className="w-[220px] h-8"
                                 data-testid={`asignar-factura-${f.id}`}>
                    <SelectValue placeholder="Asignar a un inmueble" />
                  </SelectTrigger>
                  <SelectContent>
                    {inmuebles.map((i) => (
                      <SelectItem key={i.id} value={String(i.id)}>{i.direccion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {rendimiento.gastos_sin_clasificar.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1">
                  <Receipt className="w-3 h-3" /> {g.fecha} · {g.proveedor_nombre || '—'} ·
                  {' '}{eur(g.importe_base)}
                </span>
                <div className="flex gap-1">
                  <Select onValueChange={(v) => asignarGasto(g.id, v, g.clase_gasto_inmueble)}>
                    <SelectTrigger className="w-[170px] h-8"
                                   data-testid={`asignar-gasto-inmueble-${g.id}`}>
                      <SelectValue placeholder="Inmueble" />
                    </SelectTrigger>
                    <SelectContent>
                      {inmuebles.map((i) => (
                        <SelectItem key={i.id} value={String(i.id)}>{i.direccion}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select onValueChange={(v) => asignarGasto(g.id, g.inmueble_id, v)}>
                    <SelectTrigger className="w-[190px] h-8"
                                   data-testid={`asignar-gasto-clase-${g.id}`}>
                      <SelectValue placeholder="Clase de gasto" />
                    </SelectTrigger>
                    <SelectContent>
                      {(rendimiento.clases_gasto || []).map((c) => (
                        <SelectItem key={c} value={c}>{ETIQUETA_CLASE[c] || c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {inmuebles.length === 0 && (
        <p className="text-sm text-slate-500">
          Todavía no hay ninguna ficha. Crea el inmueble, añade su contrato y asigna las facturas
          de alquiler para que se calcule el rendimiento.
        </p>
      )}

      {inmuebles.map((i) => {
        const r = porInmueble[i.id];
        return (
          <Card key={i.id} data-testid={`inmueble-${i.id}`}>
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">{i.direccion}</CardTitle>
                <p className="text-xs text-slate-500">
                  {TIPOS.find(([k]) => k === i.tipo)?.[1] || i.tipo}
                  {i.referencia_catastral ? ` · RC ${i.referencia_catastral}` : ''}
                  {i.porcentaje_titularidad !== 100 ? ` · ${i.porcentaje_titularidad} % de titularidad` : ''}
                </p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-8"
                        onClick={() => setDocs({ inmuebleId: i.id, contratoId: null,
                                                 tipo: 'escritura', titulo: i.direccion })}
                        data-testid={`inmueble-documentos-${i.id}`}>
                  <Paperclip className="w-3 h-3 mr-1" />
                  Documentos{i.documentos?.length ? ` (${i.documentos.length})` : ''}
                </Button>
                <Button size="sm" variant="outline" className="h-8"
                        onClick={() => setContrato({ ...CONTRATO_VACIO, inmueble_id: i.id })}
                        data-testid={`contrato-nuevo-${i.id}`}>
                  <Plus className="w-3 h-3 mr-1" /> Contrato
                </Button>
                <Button size="sm" variant="ghost" className="h-8"
                        onClick={() => setEditando({ ...INMUEBLE_VACIO, ...i })}
                        data-testid={`inmueble-editar-${i.id}`}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8"
                        onClick={() => borrarInmueble(i.id)}
                        data-testid={`inmueble-borrar-${i.id}`}>
                  <Trash2 className="w-3 h-3 text-red-600" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {i.contratos?.map((c) => (
                <div key={c.id} className="border rounded p-2 text-xs flex items-start justify-between gap-2"
                     data-testid={`contrato-${c.id}`}>
                  <div>
                    <p className="font-medium">
                      {c.arrendatario_nombre} {c.arrendatario_nif ? `· ${c.arrendatario_nif}` : ''}
                    </p>
                    <p className="text-slate-500">
                      {MODALIDADES.find(([k]) => k === c.modalidad)?.[1] || c.modalidad} ·
                      {' '}{eur(c.renta_mensual)}/mes
                      {c.fecha_inicio ? ` · desde ${c.fecha_inicio}` : ''}
                      {c.fecha_fin ? ` hasta ${c.fecha_fin}` : ''}
                      {c.con_retencion ? ' · con retención del 19 %' : ' · sin retención'}
                    </p>
                    <p className="text-slate-500">
                      Reducción art. 23.2: <strong>{c.reduccion_calculada?.porcentaje} %</strong>
                      {' '}({c.reduccion_calculada?.regimen}) · {c.reduccion_calculada?.motivo}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7"
                          onClick={() => setContrato({ ...CONTRATO_VACIO, ...c })}
                          data-testid={`contrato-editar-${c.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7"
                            onClick={() => setDocs({ inmuebleId: i.id, contratoId: c.id,
                                                     tipo: 'contrato',
                                                     titulo: c.arrendatario_nombre })}
                            data-testid={`contrato-documentos-${c.id}`}>
                      <Paperclip className="w-3 h-3 mr-1" />
                      {i.documentos?.filter((d) => d.contrato_id === c.id).length || 0}
                    </Button>
                  </div>
                </div>
              ))}

              {r && (
                <div className="border-t pt-2 space-y-1 text-xs" data-testid={`inmueble-calculo-${i.id}`}>
                  <div className="flex justify-between"><span>Ingresos íntegros ({r.facturas} factura/s)</span>
                    <span className="font-medium">{eur(r.ingresos)}</span></div>
                  {Object.entries(r.gastos_por_clase).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-slate-600">
                      <span>{ETIQUETA_CLASE[k] || k}</span><span>-{eur(v)}</span></div>
                  ))}
                  {r.gastos_limitados.exceso_a_trasladar > 0 && (
                    <div className="flex justify-between text-amber-700">
                      <span>Exceso de intereses y reparación que pasa a los 4 años siguientes</span>
                      <span>{eur(r.gastos_limitados.exceso_a_trasladar)}</span></div>
                  )}
                  {r.gastos_limitados.aplicado_de_anios_anteriores > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Exceso de años anteriores aplicado este año</span>
                      <span>-{eur(r.gastos_limitados.aplicado_de_anios_anteriores)}</span></div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>Amortización 3 % ({r.amortizacion.dias_arrendado} días · base {eur(r.amortizacion.base.base)})</span>
                    <span>-{eur(r.amortizacion.total)}</span></div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Rendimiento neto</span><span>{eur(r.rendimiento_neto)}</span></div>
                  {r.reduccion.porcentaje > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Reducción art. 23.2 ({r.reduccion.porcentaje} %)</span>
                      <span>-{eur(r.reduccion.importe)}</span></div>
                  )}
                  <div className="flex justify-between font-bold">
                    <span>A declarar en el modelo 100</span>
                    <span data-testid={`inmueble-reducido-${i.id}`}>{eur(r.rendimiento_reducido)}</span></div>
                  {r.retenciones_soportadas > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Retenciones del 19 % soportadas (pago a cuenta)</span>
                      <span>{eur(r.retenciones_soportadas)}</span></div>
                  )}
                  {r.revision?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {r.revision.map((aviso, n) => (
                        <p key={n} className="text-amber-700 flex gap-1">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {aviso}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* ---- ficha del inmueble ---- */}
      <Dialog open={!!editando} onOpenChange={(v) => !v && setEditando(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">
              {editando?.id ? 'Editar inmueble' : 'Nuevo inmueble'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              El valor catastral total y el de construcción salen del recibo del IBI: sirven para
              separar el suelo, que no se amortiza.
            </DialogDescription>
          </DialogHeader>
          {editando && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Dirección</Label>
                <Input value={editando.direccion} data-testid="inmueble-input-direccion"
                       onChange={(e) => setEditando({ ...editando, direccion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Referencia catastral</Label>
                <Input value={editando.referencia_catastral || ''}
                       data-testid="inmueble-input-rc"
                       onChange={(e) => setEditando({ ...editando, referencia_catastral: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Municipio</Label>
                <Input value={editando.municipio || ''}
                       onChange={(e) => setEditando({ ...editando, municipio: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={editando.tipo}
                        onValueChange={(v) => setEditando({ ...editando, tipo: v })}>
                  <SelectTrigger data-testid="inmueble-tipo-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">% de titularidad</Label>
                <Input type="number" value={editando.porcentaje_titularidad}
                       onChange={(e) => setEditando({ ...editando, porcentaje_titularidad: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Forma de adquisición</Label>
                <Select value={editando.forma_adquisicion}
                        onValueChange={(v) => setEditando({ ...editando, forma_adquisicion: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FORMAS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fecha de adquisición</Label>
                <Input type="date" value={editando.fecha_adquisicion || ''}
                       onChange={(e) => setEditando({ ...editando, fecha_adquisicion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Precio de adquisición</Label>
                <Input type="number" step="0.01" value={editando.precio_adquisicion}
                       data-testid="inmueble-input-precio"
                       onChange={(e) => setEditando({ ...editando, precio_adquisicion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Gastos y tributos de la compra</Label>
                <Input type="number" step="0.01" value={editando.gastos_adquisicion}
                       onChange={(e) => setEditando({ ...editando, gastos_adquisicion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Valor catastral total</Label>
                <Input type="number" step="0.01" value={editando.valor_catastral_total}
                       data-testid="inmueble-input-vc-total"
                       onChange={(e) => setEditando({ ...editando, valor_catastral_total: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Valor catastral de construcción</Label>
                <Input type="number" step="0.01" value={editando.valor_catastral_construccion}
                       data-testid="inmueble-input-vc-construccion"
                       onChange={(e) => setEditando({ ...editando, valor_catastral_construccion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Año de revisión catastral</Label>
                <Input type="number" value={editando.anio_revision_catastral || ''}
                       onChange={(e) => setEditando({ ...editando, anio_revision_catastral: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={!!editando.zona_tensionada}
                        onCheckedChange={(v) => setEditando({ ...editando, zona_tensionada: v })}
                        data-testid="inmueble-zona-tensionada" />
                <Label className="text-xs">En zona tensionada</Label>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notas</Label>
                <Textarea value={editando.notas || ''} rows={2}
                          onChange={(e) => setEditando({ ...editando, notas: e.target.value })} />
              </div>
              {editando.id && editando.base_amortizacion && (
                <p className="col-span-2 text-xs text-slate-500">
                  Base de amortización actual: {eur(editando.base_amortizacion.base)}
                  {' '}({editando.base_amortizacion.criterio}) → 3 % anual =
                  {' '}{eur(editando.base_amortizacion.base * 0.03)}
                </p>
              )}
              <div className="col-span-2 border-t pt-3">
                <Label className="text-xs flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> Documentos del inmueble
                </Label>
                {editando.id ? (
                  <div className="mt-2">
                    <DocumentosInmueble
                      inmuebleId={editando.id}
                      documentos={inmuebles.find((x) => x.id === editando.id)?.documentos || []}
                      tipos={tiposDocumento}
                      tipoPorDefecto="escritura"
                      onCambio={cargar}
                    />
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Guarda la ficha y te abro la ventana para adjuntar la escritura de
                    compraventa, el recibo del IBI y lo que quieras.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button onClick={guardarInmueble} data-testid="inmueble-guardar-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- contrato ---- */}
      <Dialog open={!!contrato} onOpenChange={(v) => !v && setContrato(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">
              {contrato?.id ? 'Editar contrato' : 'Nuevo contrato'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              La fecha de celebración decide el régimen de la reducción del art. 23.2 LIRPF.
              La reducción solo se aplica al arrendamiento de vivienda habitual.
            </DialogDescription>
          </DialogHeader>
          {contrato && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Arrendatario</Label>
                <Input value={contrato.arrendatario_nombre} data-testid="contrato-input-nombre"
                       onChange={(e) => setContrato({ ...contrato, arrendatario_nombre: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">NIF</Label>
                <Input value={contrato.arrendatario_nif || ''}
                       onChange={(e) => setContrato({ ...contrato, arrendatario_nif: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Tipo de arrendatario</Label>
                <Select value={contrato.arrendatario_tipo}
                        onValueChange={(v) => setContrato({ ...contrato, arrendatario_tipo: v,
                          con_retencion: v !== 'particular' })}>
                  <SelectTrigger data-testid="contrato-tipo-arrendatario"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_ARRENDATARIO.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Modalidad</Label>
                <Select value={contrato.modalidad}
                        onValueChange={(v) => setContrato({ ...contrato, modalidad: v })}>
                  <SelectTrigger data-testid="contrato-modalidad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODALIDADES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fecha de celebración</Label>
                <Input type="date" value={contrato.fecha_celebracion || ''}
                       data-testid="contrato-input-celebracion"
                       onChange={(e) => setContrato({ ...contrato, fecha_celebracion: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Renta mensual</Label>
                <Input type="number" step="0.01" value={contrato.renta_mensual}
                       data-testid="contrato-input-renta"
                       onChange={(e) => setContrato({ ...contrato, renta_mensual: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Inicio</Label>
                <Input type="date" value={contrato.fecha_inicio || ''}
                       data-testid="contrato-input-inicio"
                       onChange={(e) => setContrato({ ...contrato, fecha_inicio: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Fin</Label>
                <Input type="date" value={contrato.fecha_fin || ''}
                       onChange={(e) => setContrato({ ...contrato, fecha_fin: e.target.value })} />
              </div>
              {contrato.modalidad === 'vivienda_habitual' && (
                <>
                  <div>
                    <Label className="text-xs">Renta del contrato anterior</Label>
                    <Input type="number" step="0.01" value={contrato.renta_anterior ?? ''}
                           onChange={(e) => setContrato({ ...contrato, renta_anterior: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Índice de referencia de la zona</Label>
                    <Input type="number" step="0.01" value={contrato.indice_referencia ?? ''}
                           onChange={(e) => setContrato({ ...contrato, indice_referencia: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Nacimiento del arrendatario (para 18-35)</Label>
                    <Input type="date" value={contrato.arrendatario_nacimiento || ''}
                           onChange={(e) => setContrato({ ...contrato, arrendatario_nacimiento: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Reducción a mano (%) · vacío = calculada</Label>
                    <Input type="number" step="1" value={contrato.reduccion_manual ?? ''}
                           data-testid="contrato-input-reduccion"
                           onChange={(e) => setContrato({ ...contrato, reduccion_manual: e.target.value })} />
                  </div>
                  {[['zona_tensionada', 'En zona tensionada'],
                    ['primer_alquiler', 'Primer alquiler de la vivienda'],
                    ['gran_tenedor', 'El arrendador es gran tenedor'],
                    ['rehabilitada_2anios', 'Rehabilitada en los 2 años anteriores'],
                    ['alquiler_social', 'Alquiler social o renta limitada'],
                    ['prorroga_tacita', 'Prórroga tácita (art. 10.1 LAU)']].map(([campo, texto]) => (
                    <div key={campo} className="flex items-center gap-2">
                      <Switch checked={!!contrato[campo]} data-testid={`contrato-${campo}`}
                              onCheckedChange={(v) => setContrato({ ...contrato, [campo]: v })} />
                      <Label className="text-xs">{texto}</Label>
                    </div>
                  ))}
                </>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={!!contrato.con_retencion} data-testid="contrato-con-retencion"
                        onCheckedChange={(v) => setContrato({ ...contrato, con_retencion: v })} />
                <Label className="text-xs">El arrendatario practica la retención del 19 %</Label>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notas</Label>
                <Textarea value={contrato.notas || ''} rows={2}
                          onChange={(e) => setContrato({ ...contrato, notas: e.target.value })} />
              </div>
              {contrato.reduccion_calculada && (
                <p className="col-span-2 text-xs text-slate-500">
                  Reducción calculada ahora mismo: <strong>{contrato.reduccion_calculada.porcentaje} %</strong>
                  {' '}({contrato.reduccion_calculada.regimen}) · {contrato.reduccion_calculada.motivo}
                </p>
              )}
              <div className="col-span-2 border-t pt-3">
                <Label className="text-xs flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> Contrato firmado y anexos
                </Label>
                {contrato.id ? (
                  <div className="mt-2">
                    <DocumentosInmueble
                      inmuebleId={contrato.inmueble_id}
                      contratoId={contrato.id}
                      documentos={inmuebles.find((x) => x.id === contrato.inmueble_id)?.documentos || []}
                      tipos={tiposDocumento}
                      tipoPorDefecto="contrato"
                      onCambio={cargar}
                    />
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Guarda el contrato y te abro la ventana para adjuntar el PDF firmado.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContrato(null)}>Cancelar</Button>
            <Button onClick={guardarContrato} data-testid="contrato-guardar-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ---- documentos (escritura, contrato firmado, IBI...) ---- */}
      <Dialog open={!!docs} onOpenChange={(v) => !v && setDocs(null)}>
        <DialogContent className="max-w-[1340px] w-[95vw] h-[90vh] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              {docs?.contratoId ? 'Documentos del contrato' : 'Documentos del inmueble'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {docs?.titulo}
              {docs?.contratoId
                ? ' · contrato firmado, anexos, actualizaciones de renta y recibos.'
                : ' · escritura de compraventa, gastos de la compra, recibo del IBI, facturas de mejoras y muebles, seguro… lo que necesites.'}
            </DialogDescription>
          </DialogHeader>
          {docs && (
            <DocumentosInmueble
              inmuebleId={docs.inmuebleId}
              contratoId={docs.contratoId}
              documentos={inmuebles.find((x) => x.id === docs.inmuebleId)?.documentos || []}
              tipos={tiposDocumento}
              tipoPorDefecto={docs.tipo}
              onCambio={cargar}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocs(null)}
                    data-testid="documentos-cerrar-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InmueblesPanel;
